<template>
  <div class="dashboard-container">
    <!-- Header do Dashboard -->
    <div class="dashboard-header mb-4">
      <!-- Título -->
      <b-row class="mb-3">
        <b-col cols="12">
          <p class="text-muted mb-0">
            Visão geral das métricas e estatísticas do sistema
          </p>
        </b-col>
      </b-row>

      <!-- Filtros e Ações -->
      <b-card class="shadow-sm">
        <b-row>
          <!-- Seletor de Loja -->
          <b-col
            v-if="stores.length > 1"
            cols="12"
            md="3"
          >
            <b-form-group
              label="Selecionar Loja:"
              label-for="store-select"
              class="mb-0"
            >
              <b-form-select
                id="store-select"
                v-model="storeUuid"
                :options="storeOptions"
                size="sm"
                @change="onStoreChange"
              >
                <template #first>
                  <b-form-select-option
                    :value="''"
                    disabled
                  >
                    Escolha uma loja...
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>

          <!-- Data Início -->
          <b-col
            cols="12"
            :md="stores.length > 1 ? 3 : 4"
          >
            <b-form-group
              label="Data Início:"
              label-for="data-inicio"
              class="mb-0"
            >
              <b-form-input
                id="data-inicio"
                v-model="dataInicio"
                type="date"
                size="sm"
                :max="dataFim"
                @change="validarPeriodo"
              />
            </b-form-group>
          </b-col>

          <!-- Data Fim -->
          <b-col
            cols="12"
            :md="stores.length > 1 ? 3 : 4"
          >
            <b-form-group
              label="Data Fim:"
              label-for="data-fim"
              class="mb-0"
            >
              <b-form-input
                id="data-fim"
                v-model="dataFim"
                type="date"
                size="sm"
                :min="dataInicio"
                @change="validarPeriodo"
              />
            </b-form-group>
          </b-col>

          <!-- Botões de Ação -->
          <b-col
            cols="12"
            :md="stores.length > 1 ? 3 : 4"
            class="d-flex align-items-end"
          >
            <b-button
              variant="primary"
              size="sm"
              class="mr-2"
              :disabled="loading || !storeUuid"
              @click="aplicarFiltro"
            >
              <feather-icon
                icon="FilterIcon"
                size="14"
                class="mr-50"
              />
              Filtrar
            </b-button>
            <b-button
              variant="outline-primary"
              size="sm"
              :disabled="loading || !storeUuid"
              @click="atualizarDados"
            >
              <b-spinner
                v-if="loading"
                small
                class="mr-50"
              />
              <feather-icon
                v-else
                icon="RefreshCwIcon"
                size="14"
                class="mr-50"
              />
              {{ loading ? 'Carregando...' : 'Atualizar' }}
            </b-button>
          </b-col>
        </b-row>

        <!-- Mensagem de Erro -->
        <b-row
          v-if="mensagemErro"
          class="mt-2"
        >
          <b-col cols="12">
            <b-alert
              show
              variant="danger"
              class="mb-0"
            >
              <small>{{ mensagemErro }}</small>
            </b-alert>
          </b-col>
        </b-row>
      </b-card>
    </div>

    <!-- Cards de Estatísticas -->
    <dashboard-stats :estatisticas="estatisticas" />

    <!-- Gráficos e Tabelas -->
    <b-row>
      <!-- Gráfico de Métricas de Atendimento -->
      <metrics-chart-card
        :chart-data="dadosGrafico"
        :options="opcoesGrafico"
      />

      <!-- Campanhas WhatsApp AI -->
      <whats-app-campaigns-card :campanhas="ultimasCampanhas" />
    </b-row>

    <!-- Análise RFM -->
    <b-row v-if="Object.keys(dadosRFM.matrizRFM).length > 0">
      <RFMAnalysis
        :matriz-r-f-m="dadosRFM.matrizRFM"
        :segmentos="dadosRFM.segmentosCalculados"
        :data-inicio="dataInicio"
        :data-fim="dataFim"
        @cell-click="abrirModalClientes"
        @segment-click="abrirModalPorSegmento"
      />
    </b-row>

    <!-- Loading RFM -->
    <b-row v-else>
      <b-col
        cols="12"
        class="mb-4"
      >
        <b-card>
          <template #header>
            <h4 class="mb-0">
              Análise RFM - Segmentação de Clientes
            </h4>
          </template>
          <div class="text-center p-5">
            <b-spinner
              variant="primary"
              class="mb-3"
            />
            <p class="text-muted">
              Carregando análise RFM...
            </p>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Modal de Clientes RFM (mantido aqui, usado pelo componente RFMAnalysis) -->
    <b-modal
      id="modal-clientes-rfm"
      v-model="modalClientesAberto"
      :title="tituloModalClientes"
      size="xl"
      scrollable
      hide-footer
    >
      <div
        v-if="clientesSelecionados.length === 0"
        class="text-center p-4"
      >
        <feather-icon
          icon="UsersIcon"
          size="48"
          class="text-muted mb-3"
        />
        <h5 class="text-muted">
          Nenhum cliente encontrado
        </h5>
        <p class="text-muted">
          Esta célula não possui clientes cadastrados.
        </p>
      </div>

      <div v-else>
        <!-- Resumo do Segmento -->
        <div
          class="segment-summary mb-4 p-3 rounded"
          :class="'bg-light-' + getSegmentColorVariant(segmentoSelecionado)"
        >
          <b-row>
            <b-col
              cols="12"
              md="3"
              class="text-center mb-3 mb-md-0"
            >
              <div class="summary-item">
                <feather-icon
                  icon="UsersIcon"
                  size="20"
                  class="mb-2"
                />
                <h4 class="mb-0 font-weight-bold">
                  {{ clientesSelecionados.length }}
                </h4>
                <small class="text-muted">Clientes</small>
              </div>
            </b-col>
            <b-col
              cols="12"
              md="3"
              class="text-center mb-3 mb-md-0"
            >
              <div class="summary-item">
                <feather-icon
                  icon="ActivityIcon"
                  size="20"
                  class="mb-2"
                />
                <h4 class="mb-0 font-weight-bold">
                  R{{ recenciaSelecionada }}
                </h4>
                <small class="text-muted">Recência</small>
              </div>
            </b-col>
            <b-col
              cols="12"
              md="3"
              class="text-center mb-3 mb-md-0"
            >
              <div class="summary-item">
                <feather-icon
                  icon="RepeatIcon"
                  size="20"
                  class="mb-2"
                />
                <h4 class="mb-0 font-weight-bold">
                  F{{ frequenciaSelecionada }}
                </h4>
                <small class="text-muted">Frequência</small>
              </div>
            </b-col>
            <b-col
              cols="12"
              md="3"
              class="text-center"
            >
              <div class="summary-item">
                <feather-icon
                  icon="DollarSignIcon"
                  size="20"
                  class="mb-2"
                />
                <h4 class="mb-0 font-weight-bold">
                  R$ {{ calcularValorTotalSegmento() }}
                </h4>
                <small class="text-muted">Valor Total</small>
              </div>
            </b-col>
          </b-row>
        </div>

        <!-- Barra de Ações -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div>
            <small class="text-muted">
              Mostrando {{ clientesSelecionados.length }} cliente(s) do segmento
              <strong>{{ getSegmentDisplayName(segmentoSelecionado) }}</strong>
            </small>
          </div>
          <b-button
            variant="outline-primary"
            size="sm"
            @click="exportarClientesCSV"
          >
            <feather-icon
              icon="DownloadIcon"
              size="14"
              class="mr-50"
            />
            Exportar CSV
          </b-button>
        </div>

        <!-- Tabela de Clientes -->
        <b-table
          :items="clientesSelecionados"
          :fields="camposTabelaModal"
          responsive
          striped
          hover
          small
          class="mb-0"
        >
          <template #cell(cliente_store_id)="data">
            <div class="d-flex align-items-center">
              <b-avatar
                :text="getInitials(data.item.name || data.item.cliente_store_id)"
                :variant="getAvatarVariant(data.item.cliente_store_id)"
                size="32"
                class="mr-2"
              />
              <div>
                <strong>{{ data.item.name || 'Cliente ' + data.item.cliente_store_id }}</strong>
                <br>
                <small class="text-muted">{{ data.item.phone || data.item.email || 'Sem contato' }}</small>
              </div>
            </div>
          </template>

          <template #cell(rfm_scores)="data">
            <div class="d-flex gap-1">
              <b-badge
                variant="danger"
                pill
              >
                R{{ (data.item.rfm_scores && data.item.rfm_scores.r) || '-' }}
              </b-badge>
              <b-badge
                variant="warning"
                pill
              >
                F{{ (data.item.rfm_scores && data.item.rfm_scores.f) || '-' }}
              </b-badge>
              <b-badge
                variant="success"
                pill
              >
                M{{ (data.item.rfm_scores && data.item.rfm_scores.m) || '-' }}
              </b-badge>
            </div>
          </template>

          <template #cell(frequencia)="data">
            <span class="font-weight-bold text-primary">{{ data.item.frequencia || 0 }}</span>
          </template>

          <template #cell(valor_monetario)="data">
            <span class="font-weight-bold text-success">R$ {{ formatarMoeda(data.item.valor_monetario || 0) }}</span>
          </template>

          <template #cell(recencia_dias)="data">
            <b-badge
              :variant="getBadgeRecencia(data.item.recencia_dias)"
              pill
            >
              {{ data.item.recencia_dias || 0 }} dias
            </b-badge>
          </template>

          <template #cell(segmento)="data">
            <b-badge
              :variant="getSegmentBadgeVariant(data.item.segmento)"
              pill
              class="px-3"
            >
              {{ data.item.segmento }}
            </b-badge>
          </template>
        </b-table>
      </div>
    </b-modal>

    <!-- Análise Geográfica - Mapa do Brasil -->
    <b-row>
      <BrazilMapAnalysis
        :dados-geograficos="dadosGeograficos"
        :data-inicio="dataInicio"
        :data-fim="dataFim"
      />
    </b-row>

    <!-- Top Clientes e Produtos -->
    <b-row>
      <TopClientes
        :top-clientes="topClientes"
        :loading="loading"
        :data-inicio="dataInicio"
        :data-fim="dataFim"
      />
      <TopProdutos
        :produtos-mais-vendidos="produtosMaisVendidos"
        :loading="loading"
        :data-inicio="dataInicio"
        :data-fim="dataFim"
      />
    </b-row>

    <!-- Últimas Campanhas -->
    <b-row>
      <CampanhasTabela
        :ultimas-campanhas="ultimasCampanhas"
        :loading="loading"
      />
    </b-row>

    <!-- UTMs e Cupons -->
    <b-row class="mb-4">
      <UTMsCard
        :utms="utms"
        :loading-utms="loadingUtms"
      />
      <CuponsCard
        :coupons="coupons"
        :loading-coupons="loadingCoupons"
      />
    </b-row>

    <!-- Chat com IA -->
    <AIChatButton :store-uuid="storeUuid" />

    <!-- Modal de Boas-vindas 2026 -->
    <!--<WelcomeModal2026 />-->
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BButton, BAvatar, BBadge, BTable, BSpinner, BFormSelect, BFormSelectOption, BModal,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import { getDashboardData } from '@/functions/basics/chat.js'
import {
  getRFM, getAnalyticCampaigns, getDemographics, getTopCustomers, getBestSellingProducts,
} from '@/functions/basics/analytics.js'
import { listTransaction } from '@/functions/basics/transactions'
import stores from '../../../functions/basics/stores'
// Componentes refatorados
import DashboardStats from './components/DashboardStats.vue'
import MetricsChartCard from './components/MetricsChartCard.vue'
import WhatsAppCampaignsCard from './components/WhatsAppCampaignsCard.vue'
import RFMAnalysis from './components/RFMAnalysis.vue'
import BrazilMapAnalysis from './components/BrazilMapAnalysis.vue'
import TopClientes from './components/TopClientes.vue'
import TopProdutos from './components/TopProdutos.vue'
import CampanhasTabela from './components/CampanhasTabela.vue'
import UTMsCard from './components/UTMsCard.vue'
import CuponsCard from './components/CuponsCard.vue'
import AIChatButton from './components/AIChatButton.vue'
import WelcomeModal2026 from './components/WelcomeModal2026.vue'
// Importar funções de segmentação RFM do módulo dedicado
import {
  determinarSegmento,
  processarMatrizRFM,
  calcularTotaisPorSegmento,
  getClientesPorSegmento,
  getSegmentDisplayName as rfmGetSegmentDisplayName,
  getSegmentBadgeVariant as rfmGetSegmentBadgeVariant,
  getSegmentColorVariant as rfmGetSegmentColorVariant,
  normalizarSegmento as rfmNormalizarSegmento,
} from './services/rfmSegmentation'

// Force reload
export default {
  name: 'Dashboard',
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BAvatar,
    BBadge,
    BTable,
    BSpinner,
    BFormSelect,
    BFormSelectOption,
    BModal,
    FeatherIcon,
    DashboardStats,
    MetricsChartCard,
    WhatsAppCampaignsCard,
    RFMAnalysis,
    BrazilMapAnalysis,
    TopClientes,
    TopProdutos,
    CampanhasTabela,
    UTMsCard,
    CuponsCard,
    AIChatButton,
    WelcomeModal2026,
  },
  data() {
    return {
      stores: [],
      storeUuid: '',
      loading: false,
      error: null,
      dataInicio: this.calcularDataInicio(),
      dataFim: this.calcularDataFim(),
      mensagemErro: '',
      estatisticas: {
        totalConversas: 0,
        tempoMedioResposta: '0min',
        satisfacao: 0,
        receitaTotalIncentivada: 0,
        taxaConversao: 0,
      },
      topClientes: [],
      produtosMaisVendidos: [],
      dadosGeograficos: [],
      ultimasCampanhas: [],
      metricasAtendimento: {
        labels: ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'],
        atendimentosBot: [0, 0, 0, 0, 0, 0, 0],
        atendimentosHumano: [0, 0, 0, 0, 0, 0, 0],
      },
      dadosRFM: {
        segmentos: {},
        segmentosCalculados: {}, // Segmentos calculados pela lógica 2D
        clientes: [],
        quartis: {},
        matrizRFM: {},
        clientesPorSegmento: {}, // Clientes agrupados por segmento
      },
      chart: null,
      // Variáveis do Modal
      modalClientesAberto: false,
      // UTMs e Cupons
      utms: [],
      coupons: [],
      loadingUtms: false,
      loadingCoupons: false,
      clientesSelecionados: [],
      segmentoSelecionado: '',
      recenciaSelecionada: 0,
      frequenciaSelecionada: 0,
      camposTabelaModal: [
        { key: 'cliente_store_id', label: 'Cliente', sortable: true },
        { key: 'rfm_scores', label: 'Scores RFM', sortable: false },
        { key: 'frequencia', label: 'Compras', sortable: true },
        { key: 'valor_monetario', label: 'Valor Total', sortable: true },
        { key: 'recencia_dias', label: 'Última Compra', sortable: true },
        { key: 'segmento', label: 'Segmento', sortable: true },
      ],
    }
  },
  computed: {
    storeOptions() {
      return this.stores.map(store => ({
        value: store.uuid,
        text: store.name || `Loja ${store.uuid.substring(0, 8)}`,
      }))
    },

    dadosGrafico() {
      return {
        labels: this.metricasAtendimento.labels,
        datasets: [
          {
            label: 'Atendimentos por Bot',
            data: this.metricasAtendimento.atendimentosBot,
            borderColor: '#28C76F',
            backgroundColor: 'rgba(40, 199, 111, 0.2)',
            borderWidth: 3,
            fill: true,
            tension: 0.4,
            pointBackgroundColor: '#28C76F',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 6,
          },
          {
            label: 'Atendimentos Humanos',
            data: this.metricasAtendimento.atendimentosHumano,
            borderColor: '#7367F0',
            backgroundColor: 'rgba(115, 103, 240, 0.2)',
            borderWidth: 3,
            fill: true,
            tension: 0.4,
            pointBackgroundColor: '#7367F0',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 6,
          },
        ],
      }
    },
    opcoesGrafico() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              usePointStyle: true,
              padding: 20,
              font: {
                size: 12,
                weight: '500',
              },
            },
          },
          title: {
            display: true,
            text: 'Atendimentos por Dia da Semana',
            font: {
              size: 14,
              weight: '600',
            },
            padding: {
              bottom: 20,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: 'rgba(0,0,0,0.1)',
              drawBorder: false,
            },
            ticks: {
              stepSize: 1,
              font: {
                size: 11,
              },
            },
            title: {
              display: true,
              text: 'Quantidade de Atendimentos',
              font: {
                size: 12,
                weight: '500',
              },
            },
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              font: {
                size: 11,
                weight: '500',
              },
            },
            title: {
              display: true,
              text: 'Dias da Semana',
              font: {
                size: 12,
                weight: '500',
              },
            },
          },
        },
        interaction: {
          intersect: false,
          mode: 'index',
        },
        elements: {
          line: {
            borderJoinStyle: 'round',
          },
        },
      }
    },
    dadosGraficoRFM() {
      const { segmentos } = this.dadosRFM
      const labels = Object.keys(segmentos)
      const dados = Object.values(segmentos)

      return {
        labels,
        datasets: [{
          data: dados,
          backgroundColor: [
            '#28C76F', // Champions - Verde
            '#7367F0', // Loyal Customers - Roxo
            '#FF9F43', // At Risk - Laranja
            '#EA5455', // Outros - Vermelho
          ],
          borderWidth: 0,
          hoverOffset: 4,
        }],
      }
    },
    opcoesGraficoRFM() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              usePointStyle: true,
              padding: 20,
              font: {
                size: 12,
                weight: '500',
              },
            },
          },
          title: {
            display: true,
            text: 'Segmentação RFM de Clientes',
            font: {
              size: 14,
              weight: '600',
            },
            padding: {
              bottom: 20,
            },
          },
          tooltip: {
            callbacks: {
              label(context) {
                const label = context.label || ''
                const value = context.parsed || 0
                const total = context.dataset.data.reduce((a, b) => a + b, 0)
                const percentage = ((value / total) * 100).toFixed(1)
                return `${label}: ${value} clientes (${percentage}%)`
              },
            },
          },
        },
        cutout: '60%',
      }
    },

    tituloModalClientes() {
      const segmentoNome = this.getSegmentDisplayName(this.segmentoSelecionado)
      const total = this.clientesSelecionados.length

      // Se não tem recência e frequência específicas, é um segmento completo
      if (this.recenciaSelecionada === 0 && this.frequenciaSelecionada === 0) {
        return `${segmentoNome} - Todos os Clientes (${total} ${total === 1 ? 'cliente' : 'clientes'})`
      }

      // Se tem, é uma célula específica
      return `${segmentoNome} - R${this.recenciaSelecionada}F${this.frequenciaSelecionada} (${total} ${total === 1 ? 'cliente' : 'clientes'})`
    },
  },
  async mounted() {
    try {
      this.stores = await stores()

      if (this.stores.length === 1) {
        this.storeUuid = this.stores[0].uuid
        await this.carregarTodosDados()
      }
    } catch (error) {
      console.error('❌ Erro ao carregar lojas:', error)
    }
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy()
    }
  },
  methods: {
    async carregarTodosDados() {
      if (!this.storeUuid) {
        console.warn('⚠️ Nenhuma loja selecionada')
        return
      }

      await this.carregarDados()
      await this.carregarDadosRFM()
      await this.carregarTopCustomers()
      await this.carregarProdutosMaisVendidos()
      await this.carregarDadosGeograficos()
      await this.carregarUltimasCampanhas()
      await this.calcularValoresIncentivo()
      await this.carregarUTMsECupons()
    },

    async onStoreChange() {
      if (this.storeUuid) {
        console.log('🏪 Loja selecionada:', this.storeUuid)
        await this.carregarTodosDados()
      }
    },

    calcularDataInicio() {
      const hoje = new Date()
      const dataInicio = new Date(hoje)
      dataInicio.setDate(dataInicio.getDate() - 30)
      return dataInicio.toISOString().split('T')[0]
    },

    calcularDataFim() {
      const hoje = new Date()
      return hoje.toISOString().split('T')[0]
    },

    validarPeriodo() {
      this.mensagemErro = ''

      if (!this.dataInicio || !this.dataFim) {
        return
      }

      const inicio = new Date(this.dataInicio)
      const fim = new Date(this.dataFim)

      // Calcular diferença em dias
      const diferencaDias = Math.ceil((fim - inicio) / (1000 * 60 * 60 * 24))

      if (diferencaDias < 0) {
        this.mensagemErro = 'A data de início deve ser anterior à data de fim.'
        return false
      }

      if (diferencaDias > 90) {
        this.mensagemErro = 'O período máximo permitido é de 3 meses (90 dias).'
        return false
      }

      return true
    },

    async aplicarFiltro() {
      if (!this.validarPeriodo()) {
        return
      }

      console.log('📅 Aplicando filtro de período:', this.dataInicio, 'até', this.dataFim)
      await this.carregarTodosDados()
    },

    async carregarDados() {
      this.loading = true
      this.error = null

      try {
        const dados = await getDashboardData(this.storeUuid, this.dataInicio, this.dataFim)
        console.log('DADOOOOOOOS', dados)

        // Atualizar estatísticas
        this.estatisticas = {
          totalConversas: dados.totalAtendimentos,
          tempoMedioResposta: dados.tempoEconomizado,
          satisfacao: dados.satisfacaoAtendimento,
        }

        // Atualizar listas (dados estáticos removidos - agora carregados via API)

        // Atualizar métricas de atendimento
        if (dados.metricasAtendimento) {
          console.log('📊 Atualizando métricas de atendimento:', dados.metricasAtendimento)
          this.metricasAtendimento = dados.metricasAtendimento
        } else {
          console.log('⚠️ Nenhuma métrica de atendimento encontrada nos dados')
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error)
        // this.error = 'Erro ao carregar dados do dashboard'
        // this.$bvToast.toast('Erro ao carregar dados do dashboard', {
        //   title: 'Erro',
        //   variant: 'danger',
        //   solid: true,
        //   autoHideDelay: 5000,
        // })
      } finally {
        this.loading = false
      }
    },

    async carregarDadosRFM() {
      try {
        const RFM = await getRFM(this.storeUuid, this.dataInicio, this.dataFim)

        if (RFM && RFM.data) {
          if (RFM.data.segmentos) {
            this.dadosRFM.segmentos = RFM.data.segmentos
          }

          this.dadosRFM.clientesPorSegmento = RFM.data.clientes_por_segmento || {}

          const todosClientes = []
          if (RFM.data.clientes_por_segmento) {
            Object.entries(RFM.data.clientes_por_segmento).forEach(([, dados]) => {
              if (dados.clientes && Array.isArray(dados.clientes)) {
                todosClientes.push(...dados.clientes)
              }
            })
          }

          this.dadosRFM.clientes = todosClientes
          this.dadosRFM.quintis = RFM.data.quintis || {}

          const matrizProcessada = processarMatrizRFM({
            matriz_rfm: RFM.data.matriz_rfm,
            clientes: todosClientes,
            clientes_por_segmento: RFM.data.clientes_por_segmento,
          })

          this.dadosRFM.matrizRFM = matrizProcessada

          this.dadosRFM.segmentosCalculados = calcularTotaisPorSegmento(matrizProcessada)
        } else {
          this.carregarDadosRFMFallback()
        }
      } catch (error) {
        console.error('Erro ao carregar dados RFM:', error)
        this.carregarDadosRFMFallback()
      }
    },

    carregarDadosRFMFallback() {
      // Dados de fallback - matriz vazia
      const matrizVazia = processarMatrizRFM({
        matriz_rfm: {},
        clientes: [],
      })

      this.dadosRFM = {
        segmentos: {},
        segmentosCalculados: {},
        clientes: [],
        quartis: {
          recencia: { q1: 1, q2: 3, q3: 15 },
          frequencia: { q1: 2, q2: 1, q3: 1 },
          monetario: { q1: 899, q2: 320.5, q3: 250 },
        },
        matrizRFM: matrizVazia,
        clientesPorSegmento: {},
      }
    },

    getSegmentDisplayName(segmento) {
      return rfmGetSegmentDisplayName(segmento)
    },

    formatarMoeda(valor) {
      if (!valor) return '0,00'
      return valor.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    async calcularValoresIncentivo() {
      try {
        const response = await getAnalyticCampaigns(this.storeUuid)

        let campanhas = []

        // Verificar estrutura dos dados (mesma lógica do carregarUltimasCampanhas)
        if (response && response.data && Array.isArray(response.data)) {
          // Estrutura: response.data é um array direto
          campanhas = response.data
        } else if (response && response.data && response.data.campanhas && Array.isArray(response.data.campanhas)) {
          // Estrutura: response.data.campanhas é um array
          campanhas = response.data.campanhas
        }

        if (campanhas.length > 0) {
          // Calcular soma do valor total de todas as campanhas
          const receitaTotal = campanhas.reduce((total, item) => {
            const valorCampanha = item.estatisticas?.conversoes?.valor_total
                                   || item.conversoes?.valor_total || 0
            return total + valorCampanha
          }, 0)

          // Calcular taxa de conversão média
          const taxaConversaoMedia = campanhas.reduce((total, item) => {
            const taxaCampanha = item.estatisticas?.conversoes?.taxa_conversao
                                   || item.conversoes?.taxa_conversao || 0
            return total + taxaCampanha
          }, 0) / campanhas.length

          // Atualizar estatísticas
          this.estatisticas.receitaTotalIncentivada = receitaTotal
          this.estatisticas.taxaConversao = Math.round(taxaConversaoMedia * 100) / 100 // Arredondar para 2 casas decimais
        } else {
          console.warn('⚠️ Nenhuma campanha encontrada para cálculo de incentivo')
          this.estatisticas.receitaTotalIncentivada = 0
          this.estatisticas.taxaConversao = 0
        }
      } catch (error) {
        console.error('❌ Erro ao calcular valores de incentivo:', error)
        this.estatisticas.receitaTotalIncentivada = 0
        this.estatisticas.taxaConversao = 0
      }
    },

    async carregarDadosGeograficos() {
      try {
        const response = await getDemographics(this.storeUuid, this.dataInicio, this.dataFim)

        if (response && response.data && response.data.vendas_por_estado) {
          this.dadosGeograficos = response.data.vendas_por_estado
        } else {
          console.log('⚠️ Nenhum dado geográfico encontrado')
          this.dadosGeograficos = []
        }
      } catch (error) {
        console.error('❌ Erro ao carregar dados geográficos:', error)
        this.dadosGeograficos = []
      }
    },

    async carregarTopCustomers() {
      try {
        const response = await getTopCustomers(this.storeUuid, this.dataInicio, this.dataFim)

        if (response && response.data && response.data.top_customers) {
          this.topClientes = response.data.top_customers
        } else {
          this.topClientes = []
        }
      } catch (error) {
        console.error('❌ Erro ao carregar top customers:', error)
        this.topClientes = []
      }
    },

    async carregarProdutosMaisVendidos() {
      try {
        const response = await getBestSellingProducts(this.storeUuid, this.dataInicio, this.dataFim)

        if (response && response.data && response.data.produtos_mais_vendidos) {
          this.produtosMaisVendidos = response.data.produtos_mais_vendidos
        } else {
          this.produtosMaisVendidos = []
        }
      } catch (error) {
        console.error('❌ Erro ao carregar produtos mais vendidos:', error)
        this.produtosMaisVendidos = []
      }
    },

    async carregarUltimasCampanhas() {
      try {
        const response = await getAnalyticCampaigns(this.storeUuid)

        if (response && response.data && Array.isArray(response.data)) {
          // Estrutura: response.data é um array direto
          this.ultimasCampanhas = response.data.map((item, index) => {
            // A API retorna audiência em 'total_contatos_enviados' (pode vir como string "0" ou "000000000")
            // Se não houver ou for "0", usa 'mensagens.total' ou 'total_mensagens_enfileiradas' como fallback
            let audiencia = 0
            if (item.total_contatos_enviados && item.total_contatos_enviados !== '0' && item.total_contatos_enviados !== '000000000') {
              audiencia = parseInt(item.total_contatos_enviados) || 0
            } else if (item.mensagens?.total) {
              audiencia = item.mensagens.total
            } else if (item.total_mensagens_enfileiradas && item.total_mensagens_enfileiradas !== '0') {
              audiencia = parseInt(item.total_mensagens_enfileiradas) || 0
            }

            // A API retorna conversões em 'conversoes.valor_total' e 'conversoes.taxa_conversao'
            const valorTotal = item.conversoes?.valor_total
              || item.estatisticas?.conversoes?.valor_total
              || item.valor_total
              || item.revenue
              || item.total_revenue
              || 0

            const taxaConversao = item.conversoes?.taxa_conversao
              || item.estatisticas?.conversoes?.taxa_conversao
              || item.taxa_conversao
              || item.conversion_rate
              || 0

            return {
              // Mapear conforme a estrutura real da API
              name: item.campanha?.name || item.name || `Campanha ${index + 1}`,
              audiencia,
              valor_total: valorTotal,
              taxa_conversao: taxaConversao,
              status: item.execucoes_detalhadas && item.execucoes_detalhadas.length > 0
                ? item.execucoes_detalhadas[item.execucoes_detalhadas.length - 1].status
                : item.status || 'completed',
            }
          })
        } else if (response && response.data && response.data.campanhas && Array.isArray(response.data.campanhas)) {
          // Estrutura: response.data.campanhas é um array
          this.ultimasCampanhas = response.data.campanhas.map((item, index) => {
            // A API retorna audiência em 'total_contatos_enviados' (pode vir como string "0" ou "000000000")
            // Se não houver ou for "0", usa 'mensagens.total' ou 'total_mensagens_enfileiradas' como fallback
            let audiencia = 0
            if (item.total_contatos_enviados && item.total_contatos_enviados !== '0' && item.total_contatos_enviados !== '000000000') {
              audiencia = parseInt(item.total_contatos_enviados) || 0
            } else if (item.mensagens?.total) {
              audiencia = item.mensagens.total
            } else if (item.total_mensagens_enfileiradas && item.total_mensagens_enfileiradas !== '0') {
              audiencia = parseInt(item.total_mensagens_enfileiradas) || 0
            }

            // A API retorna conversões em 'conversoes.valor_total' e 'conversoes.taxa_conversao'
            const valorTotal = item.conversoes?.valor_total
              || item.estatisticas?.conversoes?.valor_total
              || item.valor_total
              || item.revenue
              || item.total_revenue
              || 0

            const taxaConversao = item.conversoes?.taxa_conversao
              || item.estatisticas?.conversoes?.taxa_conversao
              || item.taxa_conversao
              || item.conversion_rate
              || 0

            return {
              name: item.campanha?.name || item.name || `Campanha ${index + 1}`,
              audiencia,
              valor_total: valorTotal,
              taxa_conversao: taxaConversao,
              status: item.execucoes_detalhadas && item.execucoes_detalhadas.length > 0
                ? item.execucoes_detalhadas[item.execucoes_detalhadas.length - 1].status
                : item.status || 'completed',
            }
          })
        } else {
          this.ultimasCampanhas = []
        }
      } catch (error) {
        console.error('❌ Erro ao carregar campanhas:', error)
        this.ultimasCampanhas = []
      }
    },

    async carregarUTMsECupons() {
      this.loadingUtms = true
      this.loadingCoupons = true

      try {
        // Calcular período (últimos 30 dias)
        const dataFim = new Date()
        const dataInicio = new Date()
        dataInicio.setDate(dataInicio.getDate() - 30)

        const dataInicioStr = dataInicio.toISOString().split('T')[0]
        const dataFimStr = dataFim.toISOString().split('T')[0]

        // Buscar transações
        const infos = await listTransaction(this.storeUuid, dataInicioStr, dataFimStr)

        if (!infos || !infos.transactions) {
          this.utms = []
          this.coupons = []
          return
        }

        // Processar UTMs
        await this.processarUTMs(infos)

        // Processar Cupons
        await this.processarCupons(infos.transactions)
      } catch (error) {
        console.error('❌ Erro ao carregar UTMs e Cupons:', error)
        this.utms = []
        this.coupons = []
      } finally {
        this.loadingUtms = false
        this.loadingCoupons = false
      }
    },

    async processarUTMs(infos) {
      const utmsData = infos.utms || []

      for (const utm of utmsData) {
        utm.utm_campaigns = []

        for (const transaction of infos.transactions) {
          const campaignIndex = this.findCampaignIndex(utm.utm_campaigns, transaction.utm_campaign)

          if (utm.utm_source === transaction.utm_source
                && campaignIndex === -1
                && transaction.utm_source !== null
                && utm.utm_source !== null
                && transaction.utm_campaign !== null) {
            utm.utm_campaigns.push({
              name: transaction.utm_campaign,
              total: 1,
              amount: parseFloat(transaction.amount),
            })
          } else if (campaignIndex !== -1
                       && utm.utm_source === transaction.utm_source
                       && transaction.utm_campaign !== null
                       && utm.utm_source !== null) {
            utm.utm_campaigns[campaignIndex].total += 1
            utm.utm_campaigns[campaignIndex].amount += parseFloat(transaction.amount)
          }
        }
      }

      this.utms = utmsData
    },

    async processarCupons(transactions) {
      const couponsData = []

      for (const transaction of transactions) {
        if (transaction.coupon !== null) {
          const couponIndex = this.findCampaignIndex(couponsData, transaction.coupon)

          if (couponIndex === -1) {
            couponsData.push({
              name: transaction.coupon,
              total: 1,
              amount: parseFloat(transaction.amount),
            })
          } else {
            couponsData[couponIndex].total += 1
            couponsData[couponIndex].amount += parseFloat(transaction.amount)
          }
        }
      }

      this.coupons = couponsData
    },

    findCampaignIndex(array, name) {
      for (let i = 0; i < array.length; i++) {
        if (array[i].name === name) {
          return i
        }
      }
      return -1
    },

    async atualizarDados() {
      await this.carregarTodosDados()

      if (!this.error) {
        this.$bvToast.toast('Dados atualizados com sucesso!', {
          title: 'Dashboard',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000,
        })
      }
    },

    getBadgeVariant(status) {
      const variants = {
        Ativada: 'success',
        Desativada: 'danger',
      }
      return variants[status] || 'light'
    },

    // Métodos do Modal RFM
    abrirModalClientes(recencia, frequencia) {
      const key = `${recencia}-${frequencia}`
      const celula = this.dadosRFM.matrizRFM[key]

      if (!celula || celula.total === 0) {
        console.warn('⚠️ Nenhum cliente nesta célula')
        this.$bvToast.toast('Esta célula não possui clientes.', {
          title: 'Aviso',
          variant: 'warning',
          solid: true,
          autoHideDelay: 3000,
        })
        return
      }

      // IMPORTANTE: Usar a lógica centralizada de segmentação 2D
      const segmento = determinarSegmento(recencia, frequencia)

      this.recenciaSelecionada = recencia
      this.frequenciaSelecionada = frequencia
      this.segmentoSelecionado = segmento.nome
      this.clientesSelecionados = celula.clientes || []

      // Aviso se há mais clientes do que os carregados
      if (this.clientesSelecionados.length < celula.total) {
        const percentual = Math.round((this.clientesSelecionados.length / celula.total) * 100)
        console.log(`ℹ️ Mostrando ${this.clientesSelecionados.length} de ${celula.total} clientes (${percentual}%)`)
      }

      console.log(`📊 [Modal] Célula R${recencia}F${frequencia} - Segmento: ${segmento.nome} - ${this.clientesSelecionados.length} clientes`)
      this.modalClientesAberto = true
    },

    abrirModalPorSegmento(segmento) {
      // IMPORTANTE: Usar a função centralizada para obter clientes do segmento
      // Isso garante que não há duplicidade entre segmentos
      const clientesDoSegmento = getClientesPorSegmento(this.dadosRFM.matrizRFM, segmento)

      if (clientesDoSegmento.length === 0) {
        // Tentar buscar do backend como fallback
        const mapeamentoSegmentos = {
          Campeões: 'campeoes',
          'Clientes Leais': 'clientes_leais',
          'Potenciais Campeões': 'potenciais_campeoes',
          'Grandes Gastadores': 'grandes_gastadores',
          'Em Risco': 'em_risco',
          'Hibernando/Perdidos': 'hibernando_perdidos',
          'Recém Chegados': 'recem_chegados',
          Outros: 'outros',
        }

        const chaveSegmento = mapeamentoSegmentos[segmento] || segmento.toLowerCase().replace(/[\s/]+/g, '_')
        const dadosBackend = this.dadosRFM.clientesPorSegmento[chaveSegmento]

        if (dadosBackend?.clientes?.length > 0) {
          // Reclassificar clientes usando a lógica 2D
          const clientesReclassificados = dadosBackend.clientes.filter(cliente => {
            if (cliente.rfm_scores) {
              const seg = determinarSegmento(cliente.rfm_scores.r, cliente.rfm_scores.f)
              return seg.nome === segmento
            }
            return false
          })

          if (clientesReclassificados.length > 0) {
            this.recenciaSelecionada = 0
            this.frequenciaSelecionada = 0
            this.segmentoSelecionado = segmento
            this.clientesSelecionados = clientesReclassificados
            this.modalClientesAberto = true
            console.log(`📊 [Modal] Segmento ${segmento} (reclassificado): ${clientesReclassificados.length} clientes`)
            return
          }
        }

        const totalSegmento = this.dadosRFM.segmentosCalculados[segmento] || 0
        this.$bvToast.toast(
          `O segmento "${rfmGetSegmentDisplayName(segmento)}" tem ${totalSegmento} clientes, mas nenhum foi carregado. Tente atualizar os dados.`,
          {
            title: 'Sem Dados',
            variant: 'warning',
            solid: true,
            autoHideDelay: 5000,
          },
        )
        return
      }

      // Resetar recência e frequência para indicar que é o segmento completo
      this.recenciaSelecionada = 0
      this.frequenciaSelecionada = 0
      this.segmentoSelecionado = segmento
      this.clientesSelecionados = clientesDoSegmento

      console.log(`📊 [Modal] Segmento ${segmento}: ${clientesDoSegmento.length} clientes (sem duplicidade)`)
      this.modalClientesAberto = true
    },

    calcularValorTotalSegmento() {
      const total = this.clientesSelecionados.reduce((sum, cliente) => sum + (cliente.valor_monetario || 0), 0)
      return this.formatarMoeda(total)
    },

    getSegmentColorVariant(segmento) {
      return rfmGetSegmentColorVariant(segmento)
    },

    getSegmentBadgeVariant(segmento) {
      return rfmGetSegmentBadgeVariant(segmento)
    },

    getBadgeRecencia(dias) {
      if (dias <= 7) return 'success'
      if (dias <= 30) return 'primary'
      if (dias <= 90) return 'warning'
      return 'danger'
    },

    getInitials(nome) {
      if (!nome) return '?'

      // Se for apenas um número (ID), retorna as primeiras 2 letras
      if (/^\d+$/.test(String(nome))) {
        return String(nome).substring(0, 2)
      }

      // Se for um nome, pega as iniciais
      const palavras = String(nome).trim().split(/\s+/)
      if (palavras.length === 1) {
        return palavras[0].substring(0, 2).toUpperCase()
      }

      // Pega primeira letra do primeiro e último nome
      return (palavras[0][0] + palavras[palavras.length - 1][0]).toUpperCase()
    },

    getAvatarVariant(clienteId) {
      // Gera uma cor consistente baseada no ID do cliente
      const variants = ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark']
      const index = (clienteId || 0) % variants.length
      return variants[index]
    },

    exportarClientesCSV() {
      try {
        // Cabeçalhos do CSV
        const headers = [
          'ID Cliente',
          'Nome',
          'Telefone',
          'Email',
          'Segmento',
          'Recência (dias)',
          'Frequência',
          'Valor Monetário',
          'Score R',
          'Score F',
          'Score M',
          'Posição RFM',
        ]

        // Montar linhas do CSV
        const linhas = this.clientesSelecionados.map(cliente => [
          cliente.cliente_store_id || '',
          cliente.name || '',
          cliente.phone || '',
          cliente.email || '',
          cliente.segmento || '',
          cliente.recencia_dias || 0,
          cliente.frequencia || 0,
          (cliente.valor_monetario || 0).toFixed(2),
            cliente.rfm_scores?.r || '',
            cliente.rfm_scores?.f || '',
            cliente.rfm_scores?.m || '',
            cliente.matriz_posicao?.rfm_completo || '',
        ].map(campo => {
          // Escapar campos com vírgula ou aspas
          const campoStr = String(campo)
          if (campoStr.includes(',') || campoStr.includes('"') || campoStr.includes('\n')) {
            return `"${campoStr.replace(/"/g, '""')}"`
          }
          return campoStr
        }).join(','))

        // Montar CSV completo
        const csv = [headers.join(','), ...linhas].join('\n')

        // Criar blob e fazer download
        const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)

        const nomeArquivo = `clientes_${this.segmentoSelecionado.toLowerCase().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`

        link.setAttribute('href', url)
        link.setAttribute('download', nomeArquivo)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        this.$bvToast.toast(`${this.clientesSelecionados.length} clientes exportados com sucesso!`, {
          title: 'Exportação Concluída',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000,
        })
      } catch (error) {
        console.error('Erro ao exportar CSV:', error)
        this.$bvToast.toast('Erro ao exportar dados. Tente novamente.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000,
        })
      }
    },
  },
}
</script>

<style src="./styles/dashboard-styles.css"></style>
<style scoped>
/* Estilos inline específicos já foram movidos para arquivos separados:
   - RFM styles → styles/rfm-styles.css
   - AI Chat styles → styles/ai-chat-styles.css
   - Dashboard styles → styles/dashboard-styles.css
*/
</style>
